import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Activity, AlertOctagon, ArrowDown, ArrowUp, Check, Copy, Gauge, GitCompare, Info, Leaf, MoreHorizontal, PanelLeft, Pencil, Plus, Search, ShoppingBag, Sparkles, Square, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Markdown } from '@/components/app/Markdown';
import { useResults } from '@/state/useResults';
import { useSession } from '@/state/session';
import { readJson, writeJson } from '@/state/storage';
import { buildAssistantContext } from '@/metrics/assistantContext';
import { formatRangeShort } from '@/domain/time';
import { fmtNumber, fmtPct } from '@/domain/numbers';
import { createSession, deleteSession, groupSessions, listMessages, listSessions, renameSession, streamReply } from '@/lib/assistantApi';
import { cn } from '@/lib/utils';

/*
  Assistent-arbeidsrom i ChatGPT-stil: samtaleliste til venstre, samtale i midten, skrivefelt nederst.
  Svarene strømmes fra backend (SSE) og bygger kun på tall som allerede er beregnet i portalen for valgt
  periode (samme motor som Oversikt/Rapport) pluss bestillinger/forespørsler fra backend.
*/

const ACTIVE_KEY = 'hr:assistant-session';

/** KI-merket: Hardanger Rock-oransje sirkel med gnist. Brukes som avatar for assistenten. */
export function KiMark({ className, iconClassName }) {
  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--hr-accent)] text-white shadow-[inset_0_-1px_0_rgba(0,0,0,0.08),0_1px_2px_rgba(16,24,40,0.12)]', className)} aria-hidden="true">
      <Sparkles className={cn('h-[55%] w-[55%]', iconClassName)} strokeWidth={2} />
    </span>
  );
}

/* Startspørsmål i den rekkefølgen en driftsleder faktisk jobber: status → endring → årsak → klima → avvik → salg. */
const SUGGESTIONS = [
  { id: 'status', icon: Activity, title: 'Hvordan gikk perioden?', text: 'Gi meg en kort status for perioden: produksjon, diesel per tonn, tomgang og de viktigste avvikene.' },
  { id: 'endret', icon: GitCompare, title: 'Hva er annerledes?', text: 'Sammenlign perioden med referanseperioden. Hva har endret seg mest, og hva kan forklare det?' },
  { id: 'tomgang', icon: Gauge, title: 'Hvor går tomgangen?', text: 'Hvilke maskiner står mest på tomgang, hvor mye diesel går med til det, og hvor bør vi starte?' },
  { id: 'co2', icon: Leaf, title: 'CO₂ i perioden', text: 'Hvor mye CO₂ er knyttet til perioden, hvordan er det beregnet, og hvor sikkert er tallet?' },
  { id: 'avvik', icon: AlertOctagon, title: 'Avvik å følge opp', text: 'Hvilke avvik bør vi følge opp først, og hva bør sjekkes for hvert av dem?' },
  { id: 'salg', icon: ShoppingBag, title: 'Bestillinger og forespørsler', text: 'Hvilke bestillinger og prosjektforespørsler ligger inne, og hva bør følges opp?' },
];

/* Oppfølgingsspørsmål som alltid gir mening etter et svar. */
const FOLLOW_UPS = [
  { id: 'tabell', text: 'Vis tallene som en tabell.' },
  { id: 'forst', text: 'Hva bør vi gjøre først, og hvorfor?' },
  { id: 'ledelse', text: 'Oppsummer dette for ledelsen i tre setninger.' },
];

/** Grunnlaget som ble sendt med brukermeldingen (lagres av backend) – vises under svaret. */
function contextLabel(ctx) {
  if (!ctx?.periode) return null;
  const { fra, til } = ctx.periode;
  const period = fra && til ? formatRangeShort(fra, til) : null;
  return [period, ctx.scenario?.navn].filter(Boolean).join(' · ');
}

const SCOPE_LABEL = { production: 'Produksjonslinje', all: 'Alle maskiner' };

function fmtMetric(m) {
  if (!m || m.value === null || m.value === undefined) return '–';
  if (m.isShare) return fmtPct(m.value, m.decimals);
  return `${fmtNumber(m.value, m.decimals)} ${m.unit}`.trim();
}

function timeOf(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('nb-NO', { timeZone: 'Europe/Oslo', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

/** Måler høyden på det klebrige toppfeltet slik at arbeidsrommet fyller resten av skjermen og ruller internt. */
function useViewportHeight() {
  const [offset, setOffset] = useState(0);
  useLayoutEffect(() => {
    const el = document.querySelector('[data-testid=sticky-stack]');
    if (!el) return undefined;
    const update = () => setOffset(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);
  return offset;
}

/* ---------- Samtaleliste ---------- */

function SessionItem({ s, active, onSelect, onRename, onDelete, renaming, renameValue, setRenameValue, commitRename, cancelRename }) {
  if (renaming) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          commitRename();
        }}
        className="px-1"
      >
        <Input
          autoFocus
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancelRename();
          }}
          maxLength={120}
          aria-label="Nytt navn på samtalen"
          className="h-9 rounded-[8px] bg-white text-[14px]"
          data-testid="assistant-rename-input"
        />
      </form>
    );
  }
  return (
    <div className={cn('group relative flex items-center rounded-[8px] transition-colors duration-150', active ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06)] ring-1 ring-[var(--hr-hairline)]' : 'hover:bg-[rgba(31,33,48,0.05)]')}>
      <button
        type="button"
        onClick={() => onSelect(s.id)}
        className="min-w-0 flex-1 truncate rounded-[8px] py-2 pl-2.5 pr-9 text-left text-[14px] font-medium text-[var(--hr-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]"
        aria-current={active ? 'true' : undefined}
        title={s.title}
        data-testid={`assistant-session-${s.id}`}
      >
        {s.title || 'Ny samtale'}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn('absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-[6px] text-[var(--hr-muted)] transition-opacity duration-150 hover:bg-[rgba(31,33,48,0.06)] hover:text-[var(--hr-heading)] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] data-[state=open]:opacity-100', active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')}
            aria-label={`Valg for samtalen ${s.title}`}
            data-testid={`assistant-session-menu-${s.id}`}
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44 rounded-[10px] p-1">
          <DropdownMenuItem className="rounded-[6px]" onSelect={() => onRename(s)} data-testid="assistant-session-rename">
            <Pencil className="mr-2 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Gi nytt navn
          </DropdownMenuItem>
          <DropdownMenuItem className="rounded-[6px] text-[var(--hr-red)] focus:text-[var(--hr-red)]" onSelect={() => onDelete(s)} data-testid="assistant-session-delete">
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" /> Slett
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function SessionList({ sessions, status, activeId, onSelect, onNew, onRename, onDelete, renameState, reload }) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sessions.filter((s) => (s.title ?? '').toLowerCase().includes(q)) : sessions;
  }, [sessions, query]);
  const groups = useMemo(() => groupSessions(filtered), [filtered]);
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="assistant-session-list">
      <div className="shrink-0 space-y-2 px-3 pb-2 pt-3">
        <Button onClick={onNew} className="h-10 w-full justify-start gap-2 rounded-[12px] bg-[var(--hr-heading)] px-3 text-[14px] font-semibold text-white shadow-none transition-colors duration-150 hover:bg-[var(--hr-navy)]" data-testid="assistant-new-session-button">
          <Plus className="h-4 w-4" aria-hidden="true" /> Ny samtale
        </Button>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--hr-muted-2)]" aria-hidden="true" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Søk i samtaler" aria-label="Søk i samtaler" className="h-9 rounded-[10px] border-transparent bg-[rgba(31,33,48,0.05)] pl-8 text-[14px] shadow-none placeholder:text-[var(--hr-muted-2)] focus-visible:border-[var(--hr-focus)] focus-visible:bg-white" data-testid="assistant-session-search" />
        </div>
      </div>
      <div className="hr-scroll min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {status === 'ready' && sessions.length > 0 && filtered.length === 0 && <p className="px-2.5 pt-3 text-[14px] leading-6 text-[var(--hr-muted)]">Ingen samtaler matcher «{query}».</p>}
        {status === 'loading' && (
          <div className="space-y-2 pt-2" aria-busy="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-[8px]" />
            ))}
          </div>
        )}
        {status === 'error' && (
          <div className="rounded-[10px] border border-dashed border-[var(--hr-border)] p-3 text-[14px] text-[var(--hr-muted)]" data-testid="assistant-sessions-error">
            Kunne ikke hente samtalene.
            <Button variant="link" size="sm" className="h-auto px-1 text-[14px]" onClick={reload}>
              Prøv igjen
            </Button>
          </div>
        )}
        {status === 'ready' && sessions.length === 0 && <p className="px-2.5 pt-3 text-[14px] leading-6 text-[var(--hr-muted)]">Ingen samtaler ennå. Still et spørsmål, så lagres samtalen her.</p>}
        {groups.map((g) => (
          <div key={g.id} className="mt-3 first:mt-1">
            <div className="mb-1 px-2.5 text-[12px] font-semibold uppercase tracking-[0.1em] text-[var(--hr-muted-2)]">{g.label}</div>
            <div className="flex flex-col gap-0.5">
              {g.items.map((s) => (
                <SessionItem key={s.id} s={s} active={s.id === activeId} onSelect={onSelect} onRename={onRename} onDelete={onDelete} renaming={renameState.id === s.id} renameValue={renameState.value} setRenameValue={renameState.setValue} commitRename={renameState.commit} cancelRename={renameState.cancel} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Grunnlag ---------- */

function ContextChip({ r }) {
  if (!r.ready) return null;
  const c = r.current;
  const items = [
    ['Periode', `${formatRangeShort(r.fromDate, r.toDate)} (Europe/Oslo)`],
    ['Referanse', formatRangeShort(r.compare.fromDate, r.compare.toDate)],
    ['Scenario', `${r.scenario.name} · Eksempeldata`],
    ['Omfang', c.scopeLabel ?? SCOPE_LABEL[r.scope] ?? r.scope],
    ['Produksjon', fmtMetric(c.tonnes)],
    ['Diesel/tonn', fmtMetric(c.dieselPerTonne)],
    ['Tomgangsandel', fmtMetric(c.idleShare)],
    ['CO₂ totalt', `${fmtMetric(c.co2TotalKg)}${c.co2?.sourceLabel ? ` · ${c.co2.sourceLabel}` : ''}`],
    ['Datagrunnlag', c.basisComplete ? 'Komplett' : `Mangler: ${(c.missingFields ?? []).join(', ') || 'ukjent'}`],
  ];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="hr-tabular inline-flex h-8 max-w-full items-center gap-1.5 rounded-full border border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] px-3 text-[13px] font-medium text-[var(--hr-text)] transition-colors duration-150 hover:border-[rgba(31,33,48,0.24)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid="assistant-context-chip">
          <Info className="h-3.5 w-3.5 shrink-0 text-[var(--hr-muted)]" aria-hidden="true" />
          <span className="truncate">
            Grunnlag: {formatRangeShort(r.fromDate, r.toDate)} · {r.scenario.name}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[360px] rounded-[12px] p-0 shadow-[var(--hr-shadow-md)]" data-testid="assistant-context-popover">
        <div className="border-b border-[var(--hr-hairline)] px-4 py-3">
          <div className="text-[14px] font-semibold text-[var(--hr-heading)]">Dette får assistenten se</div>
          <p className="mt-0.5 text-[13px] leading-5 text-[var(--hr-muted)]">Nøkkeltall, maskiner, avvik og kildestatus for valgt periode – samme beregning som Oversikt og Rapport. Bestillinger og forespørsler legges til av serveren. Endre periode i filterraden.</p>
        </div>
        <dl className="divide-y divide-[var(--hr-hairline)] px-4 text-[14px]">
          {items.map(([k, v]) => (
            <div key={k} className="flex items-baseline justify-between gap-4 py-2">
              <dt className="shrink-0 text-[var(--hr-muted)]">{k}</dt>
              <dd className="hr-tabular text-right text-[var(--hr-heading)]">{v}</dd>
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  );
}

/* ---------- Meldinger ---------- */

function CopyButton({ text }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1600);
    } catch {
      toast.error('Kunne ikke kopiere');
    }
  };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" onClick={copy} className="flex h-7 w-7 items-center justify-center rounded-[6px] text-[var(--hr-muted)] transition-colors duration-150 hover:bg-[rgba(31,33,48,0.06)] hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" aria-label="Kopier svaret" data-testid="assistant-copy-button">
          {done ? <Check className="h-3.5 w-3.5 text-[var(--hr-green)]" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{done ? 'Kopiert' : 'Kopier'}</TooltipContent>
    </Tooltip>
  );
}

function UserMessage({ m }) {
  return (
    <div className="flex justify-end" data-testid="assistant-message-user">
      <div className="max-w-[min(85%,640px)] whitespace-pre-wrap rounded-[18px] rounded-br-[6px] bg-[var(--hr-surface-2)] px-4 py-2.5 text-[16px] leading-7 text-[var(--hr-heading)]">{m.content}</div>
    </div>
  );
}

function AssistantMessage({ m, streaming = false, basis = null }) {
  const empty = streaming && !m.content;
  return (
    <div className="group flex gap-3" data-testid={streaming ? 'assistant-message-streaming' : 'assistant-message-assistant'}>
      <KiMark className="mt-0.5 h-7 w-7" />
      <div className="min-w-0 flex-1">
        {m.error ? (
          <div className="rounded-[12px] border border-[rgba(180,35,24,0.25)] bg-[rgba(180,35,24,0.04)] px-4 py-3 text-[14px] leading-6 text-[var(--hr-heading)]" role="alert" data-testid="assistant-message-error">
            {m.content}
          </div>
        ) : empty ? (
          <div className="flex h-7 items-center gap-1.5 text-[14px] text-[var(--hr-muted)]" aria-live="polite">
            <span className="hr-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            Tenker …
          </div>
        ) : (
          <div className={cn('hr-chat-md', streaming && 'hr-chat-streaming')} aria-live={streaming ? 'polite' : undefined}>
            <Markdown>{m.content}</Markdown>
          </div>
        )}
        {!streaming && !m.error && (
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-[var(--hr-muted-2)]">
            <CopyButton text={m.content} />
            {m.stopped && <span className="rounded-full bg-[var(--hr-surface-2)] px-2 py-0.5 text-[12px] font-medium text-[var(--hr-muted)]">Stoppet</span>}
            {basis && (
              <span className="hr-tabular inline-flex items-center gap-1" data-testid="assistant-message-basis">
                <Info className="h-3 w-3" aria-hidden="true" /> Grunnlag {basis}
              </span>
            )}
            {m.createdAt && <span className="hr-tabular">{timeOf(m.createdAt)}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyConversation({ name, onPick, disabled, composer }) {
  const first = (name ?? '').split(' ')[0];
  return (
    <div className="mx-auto flex min-h-full w-full max-w-[760px] flex-col justify-center px-4 py-8" data-testid="assistant-empty">
      <div className="flex flex-col items-center text-center">
        <KiMark className="h-12 w-12" />
        <h2 className="mt-5 text-[26px] font-semibold leading-tight tracking-[-0.02em] text-[var(--hr-heading)] sm:text-[32px]">{first ? `Hei ${first}. ` : ''}Hva vil du vite om driften?</h2>
        <p className="mt-2 max-w-[56ch] text-[15px] leading-6 text-[var(--hr-muted)]">Svar ut fra tallene i portalen for valgt periode og innkomne bestillinger. Finner ikke på tall – men kan tolke feil, så sjekk mot visningene før du handler.</p>
      </div>
      <div className="mt-7">{composer}</div>
      <div className="mt-6 grid w-full gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {SUGGESTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              type="button"
              disabled={disabled}
              onClick={() => onPick(s.text)}
              className="group flex items-start gap-3 rounded-[14px] border border-[var(--hr-hairline)] bg-[var(--hr-bg)] px-3.5 py-3 text-left transition-[border-color,background-color,transform] duration-150 hover:-translate-y-px hover:border-[rgba(31,33,48,0.2)] hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] disabled:opacity-50"
              data-testid={`assistant-suggestion-${s.id}`}
            >
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-[var(--hr-heading)] shadow-[var(--hr-shadow-xs)] transition-colors duration-150 group-hover:bg-[var(--hr-accent-soft)] group-hover:text-[var(--hr-accent-text)]" aria-hidden="true">
                <Icon className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold text-[var(--hr-heading)]">{s.title}</span>
                <span className="mt-0.5 line-clamp-2 block text-[13px] leading-5 text-[var(--hr-muted)]">{s.text}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Skrivefelt ---------- */

function Composer({ value, onChange, onSend, onStop, streaming, disabled, inputRef, centered = false }) {
  const taRef = useRef(null);
  const setRefs = (el) => {
    taRef.current = el;
    if (inputRef) inputRef.current = el;
  };
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [value]);
  const canSend = !streaming && !disabled && value.trim().length > 0;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSend) onSend();
      }}
      className={cn('mx-auto w-full max-w-[760px]', centered ? 'px-0' : 'px-3 pb-3 pt-2 sm:px-4 sm:pb-4')}
    >
      <div className={cn('flex items-end gap-2 rounded-[22px] border border-[var(--hr-hairline)] bg-white py-2 pl-4 pr-2 shadow-[0_8px_30px_-12px_rgba(16,24,40,0.22),0_1px_2px_rgba(16,24,40,0.06)] transition-[border-color,box-shadow] duration-150 focus-within:border-[rgba(31,33,48,0.3)] focus-within:shadow-[0_12px_36px_-12px_rgba(16,24,40,0.3),0_0_0_4px_rgba(31,33,48,0.05)]', disabled && 'opacity-60')} data-testid="assistant-composer">
        <textarea
          ref={setRefs}
          rows={1}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              if (canSend) onSend();
            }
            if (e.key === 'Escape' && streaming) onStop();
          }}
          placeholder={disabled ? 'Venter på grunnlagsdata …' : 'Spør om produksjon, tomgang, CO₂, avvik eller bestillinger …'}
          aria-label="Melding til assistenten"
          disabled={disabled}
          className="hr-scroll max-h-[220px] min-h-[28px] flex-1 resize-none bg-transparent py-1 text-[16px] leading-7 text-[var(--hr-heading)] placeholder:text-[var(--hr-muted-2)] focus:outline-none disabled:cursor-not-allowed"
          data-testid="assistant-input"
        />
        {streaming ? (
          <Button type="button" onClick={onStop} className="h-9 w-9 shrink-0 rounded-full bg-[var(--hr-heading)] p-0 text-white shadow-none hover:bg-[var(--hr-navy)]" aria-label="Stopp svaret" data-testid="assistant-stop-button">
            <Square className="h-3.5 w-3.5 fill-current" aria-hidden="true" />
          </Button>
        ) : (
          <Button type="submit" disabled={!canSend} className="h-9 w-9 shrink-0 rounded-full bg-[var(--hr-heading)] p-0 text-white shadow-none transition-colors duration-150 hover:bg-[var(--hr-navy)] disabled:bg-[var(--hr-inset)] disabled:text-[var(--hr-muted-2)] disabled:opacity-100" aria-label="Send melding" data-testid="assistant-send-button">
            <ArrowUp className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>
      {!centered && <p className="mt-2 hidden text-center text-[13px] leading-5 text-[var(--hr-muted-2)] sm:block">Enter sender · Shift+Enter gir ny linje · Svarene gjelder valgt periode og kan inneholde feil</p>}
    </form>
  );
}

/* ---------- Siden ---------- */

export default function AssistentPage() {
  const r = useResults();
  const { session, role } = useSession();
  const offset = useViewportHeight();

  const [sessions, setSessions] = useState([]);
  const [sessionsStatus, setSessionsStatus] = useState('loading');
  const [activeId, setActiveId] = useState(() => readJson(window.localStorage, ACTIVE_KEY, null));
  const [messages, setMessages] = useState([]);
  const [messagesStatus, setMessagesStatus] = useState('idle');
  const [draft, setDraft] = useState('');
  const [stream, setStream] = useState(null); // { id, content, error }
  const [listOpen, setListOpen] = useState(false);
  const [rename, setRename] = useState({ id: null, value: '' });
  const [toDelete, setToDelete] = useState(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const stickRef = useRef(true);
  const [atBottom, setAtBottom] = useState(true);
  const inputRef = useRef(null);

  const selectSession = useCallback((id) => {
    setActiveId(id);
    writeJson(window.localStorage, ACTIVE_KEY, id);
    setListOpen(false);
  }, []);

  const loadSessions = useCallback(async () => {
    setSessionsStatus('loading');
    try {
      const list = await listSessions();
      setSessions(list);
      setSessionsStatus('ready');
      return list;
    } catch {
      setSessionsStatus('error');
      return null;
    }
  }, []);

  useEffect(() => {
    document.title = 'KI-assistent · Hardanger Rock';
    loadSessions().then((list) => {
      if (list && activeId && !list.some((s) => s.id === activeId)) selectSession(null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      setMessagesStatus('idle');
      return undefined;
    }
    let cancelled = false;
    setMessagesStatus('loading');
    listMessages(activeId)
      .then((list) => {
        if (cancelled) return;
        setMessages(list);
        setMessagesStatus('ready');
        stickRef.current = true;
      })
      .catch(() => {
        if (!cancelled) setMessagesStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  /* Rull til bunnen når nye tegn kommer – men ikke hvis brukeren har rullet opp for å lese. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !stickRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, stream?.content]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickRef.current = near;
    setAtBottom(near);
  };
  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = true;
    setAtBottom(true);
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const send = useCallback(
    async (text) => {
      const content = (text ?? draft).trim();
      if (!content || stream || !r.ready) return;
      let sid = activeId;
      try {
        if (!sid) {
          const created = await createSession();
          sid = created.id;
          setSessions((s) => [created, ...s]);
          selectSession(sid);
          setMessages([]);
          setMessagesStatus('ready');
        }
      } catch (e) {
        toast.error('Kunne ikke opprette samtale', { description: e.message });
        return;
      }
      setDraft('');
      stickRef.current = true;
      const tempUser = { id: `tmp-${Date.now()}`, role: 'user', content, createdAt: new Date().toISOString() };
      setMessages((m) => [...m, tempUser]);
      const ctx = buildAssistantContext({ current: r.current, reference: r.reference, comparison: r.comparison, deviations: r.deviations, scenario: r.scenario, fromDate: r.fromDate, toDate: r.toDate, compare: r.compare, compareMode: r.compareMode, scope: r.scope, role });
      const controller = new AbortController();
      abortRef.current = controller;
      let acc = '';
      let hadError = false;
      let assistantId = null;
      setStream({ id: null, content: '', error: false });
      try {
        await streamReply(sid, { content, context: ctx }, {
          signal: controller.signal,
          onStart: (d) => {
            assistantId = d?.messageId ?? null;
            setMessages((m) => m.map((x) => (x.id === tempUser.id && d?.userMessageId ? { ...x, id: d.userMessageId } : x)));
          },
          onDelta: (delta, { error }) => {
            acc += delta;
            if (error) hadError = true;
            setStream({ id: assistantId, content: acc, error: hadError });
          },
        });
        setMessages((m) => [...m, { id: assistantId ?? `a-${Date.now()}`, role: 'assistant', content: acc, createdAt: new Date().toISOString(), error: hadError }]);
      } catch (e) {
        if (e?.name === 'AbortError') {
          if (acc) setMessages((m) => [...m, { id: assistantId ?? `a-${Date.now()}`, role: 'assistant', content: acc, createdAt: new Date().toISOString(), stopped: true }]);
          else setMessages((m) => m.filter((x) => x.id !== tempUser.id));
        } else {
          setMessages((m) => [...m, { id: `err-${Date.now()}`, role: 'assistant', content: `Fikk ikke svar fra assistenten: ${e.message}`, createdAt: new Date().toISOString(), error: true }]);
        }
      } finally {
        abortRef.current = null;
        setStream(null);
        // Tittel settes av backend ved første melding – hent listen på nytt i bakgrunnen.
        listSessions()
          .then((list) => setSessions(list))
          .catch(() => {});
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    },
    [draft, stream, r, activeId, role, selectSession],
  );

  const newSession = () => {
    stop();
    selectSession(null);
    setDraft('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const startRename = (s) => setRename({ id: s.id, value: s.title ?? '' });
  const commitRename = async () => {
    const { id, value } = rename;
    if (!id) return;
    setRename({ id: null, value: '' });
    const title = value.trim();
    if (!title) return;
    try {
      const updated = await renameSession(id, title);
      setSessions((list) => list.map((s) => (s.id === id ? { ...s, ...updated } : s)));
    } catch (e) {
      toast.error('Kunne ikke endre navn', { description: e.message });
    }
  };
  const confirmDelete = async () => {
    const s = toDelete;
    setToDelete(null);
    if (!s) return;
    try {
      if (s.id === activeId) {
        stop();
        selectSession(null);
      }
      await deleteSession(s.id);
      setSessions((list) => list.filter((x) => x.id !== s.id));
      toast.success('Samtalen er slettet');
    } catch (e) {
      toast.error('Kunne ikke slette samtalen', { description: e.message });
    }
  };

  const active = sessions.find((s) => s.id === activeId) ?? null;
  const renameState = { id: rename.id, value: rename.value, setValue: (v) => setRename((x) => ({ ...x, value: v })), commit: commitRename, cancel: () => setRename({ id: null, value: '' }) };
  const listProps = { sessions, status: sessionsStatus, activeId, onSelect: selectSession, onNew: newSession, onRename: startRename, onDelete: setToDelete, renameState, reload: loadSessions };
  const showEmpty = !activeId || (messagesStatus === 'ready' && messages.length === 0 && !stream);

  const composer = (centered) => <Composer value={draft} onChange={setDraft} onSend={() => send()} onStop={stop} streaming={!!stream} disabled={!r.ready} inputRef={inputRef} centered={centered} />;

  return (
    <div className="flex w-full flex-none overflow-hidden bg-[var(--hr-bg)]" style={{ height: `calc(100dvh - ${offset}px)`, maxHeight: `calc(100dvh - ${offset}px)` }} data-testid="assistant-page">
      {/* Samtaleliste (desktop) – alltid synlig, ruller internt */}
      <aside className="hidden w-[276px] shrink-0 border-r border-[var(--hr-hairline)] lg:block" aria-label="Samtaler">
        <SessionList {...listProps} />
      </aside>

      {/* Samtaleflate: kort på rolig bakgrunn. Tråden ruller internt, skrivefeltet ligger alltid nederst. */}
      <section className="flex min-w-0 flex-1 flex-col lg:p-3" aria-label="Samtale">
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-white lg:rounded-[18px] lg:border lg:border-[var(--hr-hairline)] lg:shadow-[var(--hr-shadow-xs)]">
          <header className="flex h-12 shrink-0 items-center gap-2 border-b border-[var(--hr-hairline)] px-2.5 sm:px-4">
            <Sheet open={listOpen} onOpenChange={setListOpen}>
              <Button variant="ghost" size="icon" className="h-9 w-9 rounded-[10px] lg:hidden" onClick={() => setListOpen(true)} aria-label="Vis samtaler" data-testid="assistant-list-toggle">
                <PanelLeft className="h-[18px] w-[18px]" aria-hidden="true" />
              </Button>
              <SheetContent side="left" className="w-[300px] bg-[var(--hr-bg)] p-0" data-testid="assistant-list-sheet">
                <SheetHeader className="sr-only">
                  <SheetTitle>Samtaler</SheetTitle>
                </SheetHeader>
                <div className="flex h-12 items-center gap-2 border-b border-[var(--hr-hairline)] px-4 text-[14px] font-semibold text-[var(--hr-heading)]">
                  <KiMark className="h-5 w-5" /> KI-assistent
                </div>
                <div className="h-[calc(100%-48px)]">
                  <SessionList {...listProps} />
                </div>
              </SheetContent>
            </Sheet>
            <div className="flex min-w-0 items-center gap-2">
              <KiMark className="hidden h-6 w-6 sm:inline-flex" />
              <h1 className="min-w-0 truncate text-[14px] font-semibold text-[var(--hr-heading)]" data-testid="assistant-title">
                {active?.title || 'Ny samtale'}
              </h1>
            </div>
            <div className="ml-auto flex min-w-0 items-center gap-1.5">
              <ContextChip r={r} />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9 rounded-[10px]" onClick={newSession} aria-label="Ny samtale" data-testid="assistant-new-session-icon">
                    <Plus className="h-[18px] w-[18px]" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Ny samtale</TooltipContent>
              </Tooltip>
            </div>
          </header>

          {showEmpty ? (
            <div className="hr-scroll min-h-0 flex-1 overflow-y-auto" data-testid="assistant-thread">
              <EmptyConversation name={session?.displayName} onPick={(t) => send(t)} disabled={!r.ready} composer={composer(true)} />
            </div>
          ) : (
            <>
              <div ref={scrollRef} onScroll={onScroll} className="hr-scroll min-h-0 flex-1 overflow-y-auto" data-testid="assistant-thread">
                <div className="mx-auto flex w-full max-w-[760px] flex-col gap-6 px-4 pb-36 pt-6 sm:pb-40">
                  {messagesStatus === 'loading' && (
                    <div className="space-y-4" aria-busy="true">
                      <Skeleton className="ml-auto h-10 w-2/3 rounded-[18px]" />
                      <Skeleton className="h-24 w-full rounded-[12px]" />
                    </div>
                  )}
                  {messagesStatus === 'error' && (
                    <div className="rounded-[12px] border border-dashed border-[var(--hr-border)] p-4 text-[14px] text-[var(--hr-muted)]" data-testid="assistant-messages-error">
                      Kunne ikke hente samtalen.{' '}
                      <Button variant="link" className="h-auto px-1 text-[14px]" onClick={() => selectSession(activeId)}>
                        Prøv igjen
                      </Button>
                    </div>
                  )}
                  {messages.map((m, i) => {
                    if (m.role === 'user') return <UserMessage key={m.id} m={m} />;
                    const prevUser = [...messages.slice(0, i)].reverse().find((x) => x.role === 'user');
                    return <AssistantMessage key={m.id} m={m} basis={contextLabel(prevUser?.context) ?? (r.ready && i === messages.length - 1 && !prevUser?.context ? `${formatRangeShort(r.fromDate, r.toDate)} · ${r.scenario.name}` : null)} />;
                  })}
                  {stream && <AssistantMessage m={stream} streaming />}
                  {!stream && messages.length > 0 && messages[messages.length - 1].role === 'assistant' && !messages[messages.length - 1].error && (
                    <div className="flex flex-wrap gap-2 pl-10" data-testid="assistant-follow-ups">
                      {FOLLOW_UPS.map((f) => (
                        <button key={f.id} type="button" onClick={() => send(f.text)} disabled={!r.ready} className="h-8 rounded-full border border-[var(--hr-hairline)] bg-white px-3 text-[13px] font-medium text-[var(--hr-text)] transition-colors duration-150 hover:border-[rgba(31,33,48,0.24)] hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] disabled:opacity-50" data-testid={`assistant-follow-up-${f.id}`}>
                          {f.text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Skrivefeltet flyter over bunnen av tråden med en myk fade, så det alltid er synlig. */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.92)_38%,#fff_100%)] pt-12">
                {!atBottom && (
                  <div className="pointer-events-auto mb-2 flex justify-center">
                    <button type="button" onClick={scrollToBottom} className="flex h-8 w-8 items-center justify-center rounded-full border border-[var(--hr-hairline)] bg-white text-[var(--hr-heading)] shadow-[var(--hr-shadow-sm)] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" aria-label="Til bunnen av samtalen" data-testid="assistant-scroll-bottom">
                      <ArrowDown className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                )}
                <div className="pointer-events-auto">{composer(false)}</div>
              </div>
            </>
          )}
        </div>
      </section>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="assistant-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette samtalen?</AlertDialogTitle>
            <AlertDialogDescription>«{toDelete?.title}» og alle meldingene i den fjernes. Dette kan ikke angres.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="assistant-delete-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[var(--hr-red)] text-white hover:bg-[var(--hr-red)]/90" data-testid="assistant-delete-confirm">
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
